import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';
import clsx from 'clsx';

interface SandboxMessage {
  message_id: string;
  direction: 'inbound' | 'outbound';
  author: string;
  text: string | null;
  created_at: string;
}

interface ChatResponse {
  reply: string;
  tool_trace: Array<{ name: string; args: unknown; result: unknown; ok: boolean; error?: string }>;
  escalated: boolean;
  language_used: string;
  tools_available: string[];
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function Sandbox() {
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [attachedImage, setAttachedImage] = useState<{ dataUrl: string; name: string } | null>(null);
  const [lastTrace, setLastTrace] = useState<ChatResponse['tool_trace']>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: history } = useQuery({
    queryKey: ['sandbox', 'history'],
    queryFn: () => api.get<{ messages: SandboxMessage[] }>('/sandbox/history'),
  });

  const sendMutation = useMutation({
    mutationFn: (args: { message: string; image_data_url: string | null }) =>
      api.post<ChatResponse>('/sandbox/chat', args),
    onSuccess: (resp) => {
      setDraft('');
      setAttachedImage(null);
      if (fileRef.current) fileRef.current.value = '';
      setLastTrace(resp.tool_trace);
      qc.invalidateQueries({ queryKey: ['sandbox', 'history'] });
    },
  });

  function send() {
    const text = draft.trim();
    if (!text && !attachedImage) return;
    if (sendMutation.isPending) return;
    sendMutation.mutate({ message: text, image_data_url: attachedImage?.dataUrl ?? null });
  }

  async function pickFile(file: File) {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 8 * 1024 * 1024) return;
    const dataUrl = await fileToDataUrl(file);
    setAttachedImage({ dataUrl, name: file.name });
  }

  const resetMutation = useMutation({
    mutationFn: () => api.post('/sandbox/reset'),
    onSuccess: () => {
      setLastTrace([]);
      setAttachedImage(null);
      qc.invalidateQueries({ queryKey: ['sandbox', 'history'] });
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [history?.messages.length, sendMutation.isPending]);

  return (
    <div className="h-full flex flex-col">
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Sandbox</h1>
          <p className="text-xs text-slate-500">
            Talk to your AI directly. Uses your real business config and catalog — no WhatsApp/Instagram needed.
            Tool actions (create order, check inventory) write to real tables.
          </p>
        </div>
        <button
          onClick={() => resetMutation.mutate()}
          disabled={resetMutation.isPending}
          className="text-sm rounded-md border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
        >
          Reset conversation
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-4 bg-slate-50 space-y-2">
          {(history?.messages ?? []).length === 0 && !sendMutation.isPending && (
            <div className="text-sm text-slate-400 text-center py-12">
              Send a message to start a conversation with your AI. Try something a real customer might ask:
              <ul className="mt-3 text-left max-w-md mx-auto space-y-1">
                <li>• "شلونچ، عندكم فساتين سهرة بحدود 50 ألف؟"</li>
                <li>• "Hi, what time do you open tomorrow?"</li>
                <li>• "أريد أحجز موعد للأسبوع الجاي"</li>
              </ul>
            </div>
          )}
          {(history?.messages ?? []).map((m) => (
            <Bubble key={m.message_id} m={m} />
          ))}
          {sendMutation.isPending && (
            <div className="flex justify-start">
              <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-slate-400">
                Thinking…
              </div>
            </div>
          )}
          {sendMutation.error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
              Error: {(sendMutation.error as Error).message}
            </div>
          )}
        </div>

        {lastTrace.length > 0 && (
          <aside className="w-80 border-l border-slate-200 bg-white overflow-auto p-4">
            <h3 className="font-medium text-sm mb-2">Tool calls (last turn)</h3>
            <ul className="space-y-3">
              {lastTrace.map((t, i) => (
                <li key={i} className="rounded-md border border-slate-200 p-2 text-xs">
                  <div className={clsx('font-semibold', t.ok ? 'text-green-700' : 'text-red-700')}>
                    {t.name} {t.ok ? '✓' : '✕'}
                  </div>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-slate-500">args</summary>
                    <pre className="mt-1 bg-slate-50 p-1 overflow-auto text-[10px]">{JSON.stringify(t.args, null, 2)}</pre>
                  </details>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-slate-500">{t.ok ? 'result' : 'error'}</summary>
                    <pre className="mt-1 bg-slate-50 p-1 overflow-auto text-[10px]">{t.ok ? JSON.stringify(t.result, null, 2) : t.error}</pre>
                  </details>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-white p-3">
        {attachedImage && (
          <div className="mb-2 inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1">
            <img src={attachedImage.dataUrl} alt="" className="w-10 h-10 object-cover rounded" />
            <div className="text-xs text-slate-600 max-w-[180px] truncate">{attachedImage.name}</div>
            <button
              type="button"
              onClick={() => { setAttachedImage(null); if (fileRef.current) fileRef.current.value = ''; }}
              className="text-xs text-red-600 hover:underline"
            >
              remove
            </button>
          </div>
        )}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-end gap-2"
        >
          <label className="rounded-md border border-slate-300 px-2 py-2 text-sm hover:bg-slate-50 cursor-pointer" title="Attach image">
            📎
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && pickFile(e.target.files[0])}
            />
          </label>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={attachedImage ? 'Add a message about this image (optional)…' : 'Type a message as if you were a customer…'}
            rows={2}
            className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <button
            type="submit"
            disabled={(!draft.trim() && !attachedImage) || sendMutation.isPending}
            className="rounded-md bg-brand-600 text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </footer>
    </div>
  );
}

function Bubble({ m }: { m: SandboxMessage }) {
  const isOut = m.direction === 'outbound';
  return (
    <div className={clsx('flex', isOut ? 'justify-start' : 'justify-end')}>
      <div className={clsx(
        'max-w-[70%] rounded-lg px-3 py-2 text-sm shadow-sm whitespace-pre-wrap',
        isOut ? 'bg-white border border-slate-200' : 'bg-brand-600 text-white',
      )}>
        {m.text}
      </div>
    </div>
  );
}
